const FILTER_LAYOUT = 'filter';
const NO_CATEGORY = 'NO_CATEGORY';

interface NamedAttribute {
    name: string;
}

/**
 * Attributes offered by the item search filter and bulk search pickers.
 * A defined `filter` layout is the source list. A selected category keeps
 * only the attributes that also belong to that category.
 */
export interface SearchFilterAttributeSource {
    filteredLayoutAttributes?: (layoutName: string) => NamedAttribute[];
    allAttributes?: () => NamedAttribute[];
    categoryAttributes?: (category: string) => NamedAttribute[];
}

export function resolveSearchFilterAttributes<T extends NamedAttribute>(
    dataModel: SearchFilterAttributeSource | null | undefined,
    category?: string | null,
): T[] {
    if (!dataModel) {
        return [];
    }

    const fromLayout = (dataModel.filteredLayoutAttributes?.(FILTER_LAYOUT) ?? []) as T[];
    const attributes = fromLayout.length > 0
        ? fromLayout
        : (dataModel.allAttributes?.() ?? []) as T[];

    if (!category || category === NO_CATEGORY) {
        return uniqueByName(attributes);
    }

    const categoryNames = new Set(
        (dataModel.categoryAttributes?.(category) ?? []).map((attribute) => attribute.name),
    );
    return uniqueByName(attributes.filter((attribute) => categoryNames.has(attribute.name)));
}

function uniqueByName<T extends NamedAttribute>(attributes: T[]): T[] {
    const seen = new Set<string>();
    return attributes.filter((attribute) => {
        if (seen.has(attribute.name)) {
            return false;
        }
        seen.add(attribute.name);
        return true;
    });
}
